import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LogOut } from 'lucide-react';

export const Settings: React.FC = () => {
  const { user, isLoading, isAuthenticated, logout } = useAuth0();

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto pt-5 pb-8">
          <div className="h-10 w-40 bg-foreground/5 rounded animate-pulse mb-8" />
          <div className="space-y-12">
            <div className="h-32 bg-foreground/5 rounded animate-pulse" />
            <div className="h-32 bg-foreground/5 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto pt-5 pb-8">
          <h2 className="text-2xl font-medium mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground">Please log in to access settings.</p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const auth0ProfileUrl = `https://${import.meta.env.VITE_AUTH0_DOMAIN}/u/profile`;

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Account and security options, managed through Auth0.
          </p>
        </div>

        <div className="space-y-12">
          {/* Account */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Account</h2>
            </div>
            <div className="flex-1 min-w-0">
              <dl className="space-y-4">
                <div className="flex items-center">
                  <dt className="text-sm font-medium text-muted-foreground w-40 flex-shrink-0">Profile information</dt>
                  <dd><Badge variant="default">Managed by Auth0</Badge></dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-sm font-medium text-muted-foreground w-40 flex-shrink-0">Email verification</dt>
                  <dd>
                    <Badge variant={user?.email_verified ? 'success' : 'destructive'}>
                      {user?.email_verified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </dd>
                </div>
              </dl>
              <a
                href={auth0ProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-sm font-medium text-foreground hover:underline"
              >
                Manage account in Auth0 ↗
              </a>
            </div>
          </section>

          {/* Security */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Security</h2>
            </div>
            <div className="flex-1 min-w-0">
              <dl className="space-y-4">
                <div className="flex items-center">
                  <dt className="text-sm font-medium text-muted-foreground w-40 flex-shrink-0">Two-factor auth</dt>
                  <dd><Badge variant="default">Managed by Auth0</Badge></dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-sm font-medium text-muted-foreground w-40 flex-shrink-0">Password</dt>
                  <dd><Badge variant="default">Managed by Auth0</Badge></dd>
                </div>
              </dl>
            </div>
          </section>

          {/* Session */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Session</h2>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground mb-4">
                Signed in as <span className="text-foreground font-medium break-all">{user?.email}</span>
              </p>
              <Button variant="tertiary" size="dense" onClick={handleLogout}>
                <LogOut className="h-4 w-4 stroke-[1.5]" />
                Logout
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Settings;
